import React from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import AddRemoveControl from '@/components/common/AddRemoveControl';
import ControlledCheckbox from '@/components/common/form/ControlledCheckbox';
import ControlledTextField from '@/components/common/form/ControlledTextField';
import ControlledTextSelectField from '@/components/common/form/ControlledTextSelectField';
import CustomFieldTypes from '@/constants/customFields/CustomFieldTypes';
import useCustomFields from '@/lib/hooks/swr/useCustomFields';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { Dialogs, useDialogManager } from '@/modules/Dialogs';
import type CustomField from '@/types/api/CustomField';
import showErrorMessage from '@/utils/toasts/showErrorMessage';
import showSuccessMessage from '@/utils/toasts/showSuccessMessage';
import {
  FIELD_TYPE_OPTIONS,
  RESPONSE_TYPE_OPTIONS,
  SELECT,
} from '../../config/customFieldsConfig';
import type CustomFieldsFormData from '../../types/CustomFieldsFormData';

const CLASSES = { select: { formControl: 'md:w-1/2' } };

export default function EditCustomField({
  field,
  setExpandedItem,
}: {
  field: CustomField;
  setExpandedItem: (fieldId: string) => void;
}) {
  const { openDialog, closeDialog } = useDialogManager();
  const {
    customFieldId,
    fieldName,
    fieldOptions,
    fieldType,
    isRequired,
    sortOrder,
  } = field;
  const { remove: apiRemove, update } = useCustomFields();

  const isSelectType =
    fieldType === CustomFieldTypes.LIST_SINGLE ||
    fieldType === CustomFieldTypes.LIST_MULTI;

  const getText = (type: string, arr: { value: string; text: string }[]) =>
    arr.find((opt) => opt.value === type)?.text || undefined;

  const mapFieldOptions = (options?: string[]) =>
    options?.map((opt) => ({ name: opt })) || [];

  const { control, formState, handleSubmit } = useForm<CustomFieldsFormData>({
    defaultValues: {
      fieldName,
      fieldOptions: mapFieldOptions(fieldOptions),
      fieldResponseType: {
        text: isSelectType
          ? getText(fieldType, RESPONSE_TYPE_OPTIONS)
          : undefined,
        value: isSelectType ? fieldType : undefined,
      },
      fieldType: {
        text: getText(isSelectType ? SELECT : fieldType, FIELD_TYPE_OPTIONS),
        value: isSelectType ? SELECT : fieldType,
      },
      isRequired,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'fieldOptions',
  });

  React.useEffect(() => {
    if (!fields.length) {
      append({ name: '' });
    }
  }, [fields]);

  const submit = React.useCallback(
    async (formData: CustomFieldsFormData) => {
      if (formState.isSubmitting) return;
      const {
        fieldName: formFieldName,
        fieldOptions: formFieldOptions,
        isRequired: formIsRequired,
      } = formData;

      const apiData = {
        customFieldId,
        fieldName: formFieldName,
        fieldOptions: isSelectType
          ? formFieldOptions?.map((opt) => opt.name)
          : [],
        isRequired: formIsRequired,
        sortOrder,
      };

      try {
        await update(customFieldId, apiData);
        showSuccessMessage({
          message: translate('CUSTOM_FIELDS_EDIT_SUCCESS'),
        });
      } catch (error) {
        showErrorMessage({ message: translate('CUSTOM_FIELDS_EDIT_ERROR') });
      }
    },
    [formState],
  );

  const openDeleteConfirmation = React.useCallback(() => {
    openDialog(Dialogs.CONFIRMATION, {
      confirmLabel: translate('DELETE'),
      headerText: translate('CUSTOM_FIELDS_DELETE'),
      onCancel: closeDialog,
      onClose: closeDialog,
      onConfirm() {
        try {
          apiRemove(customFieldId);
          closeDialog();
          showSuccessMessage({
            message: translate('CUSTOM_FIELDS_DELETE_SUCCESS'),
          });
        } catch (error) {
          showErrorMessage({
            message: translate('CUSTOM_FIELDS_DELETE_ERROR'),
          });
        }
      },
    });
  }, []);

  return (
    <form
      className="flex flex-col gap-4 px-14 py-2"
      data-testid="edit-custom-field-form"
      onSubmit={handleSubmit(submit)}
    >
      <div className="flex flex-col gap-4 md:flex-row">
        <ControlledTextField
          className="md:w-1/2"
          control={control}
          label={translate('CUSTOM_FIELDS_FIELD_NAME')}
          name="fieldName"
          required
          value={fieldName}
        />
        <ControlledTextSelectField
          classes={CLASSES.select}
          control={control}
          disabled
          items={FIELD_TYPE_OPTIONS}
          label={translate('CUSTOM_FIELDS_FIELD_TYPE')}
          name="fieldType"
          required
        />
      </div>
      {isSelectType && (
        <div className="flex flex-col gap-4">
          <div className="text-sm font-semibold">
            {translate('CUSTOM_FIELDS_LIST_OPTIONS')}
          </div>
          {fields.map((item, i, arr) => (
            <div className="flex items-center" key={item.id}>
              <ControlledTextField
                className="w-4/5"
                control={control}
                disabled={fieldOptions?.includes(item?.name)}
                name={`fieldOptions.${i}.name`}
                required={i === 0}
                rules={{
                  pattern: {
                    value: /^[^,]*$/,
                    message: translate('CUSTOM_FIELDS_OPTION_VALIDATION'),
                  },
                }}
                error={!!formState.errors.fieldOptions?.[i]?.name}
                helperText={formState.errors.fieldOptions?.[i]?.name?.message}
              />
              <AddRemoveControl
                className="ml-4 flex"
                onAdd={() => append({ name: '' })}
                onRemove={() => remove(i)}
                showAdd={i === arr.length - 1}
                showRemove={arr.length > 1}
              />
            </div>
          ))}
          <div className="text-sm font-semibold">
            {translate('CUSTOM_FIELDS_LIST_VALIDATION')}
          </div>
          <ControlledTextSelectField
            control={control}
            fullWidth
            label={translate('CUSTOM_FIELDS_RESPONSE_TYPE')}
            name="fieldResponseType"
            required
            disabled
            items={RESPONSE_TYPE_OPTIONS}
          />
        </div>
      )}
      <div className="flex justify-between">
        <ControlledCheckbox
          control={control}
          label={translate('CUSTOM_FIELDS_MANDATORY')}
          name="isRequired"
        />
        <div>
          <Button onClick={openDeleteConfirmation} color="error">
            {translate('CUSTOM_FIELDS_DELETE_FIELD')}
          </Button>
          <Button
            onClick={() => {
              setExpandedItem('');
            }}
          >
            {translate('CANCEL')}
          </Button>
          <Button type="submit" variant="contained">
            {translate('SAVE')}
          </Button>
        </div>
      </div>
    </form>
  );
}
